// importParameterContent.js
import * as XLSX from "xlsx";

/**
 * Rebuilds the complex JSON from the flat Excel rows.
 */
const transformRowsToParameterJson = (rows) => {
  const finalJson = {};
  
  // Group rows by Parameter Name AND Section
  const groups = {};
  for (const row of rows) {
    if (!row["Parameter Name"] || !row["Section"]) continue;
    const key = `${row["Parameter Name"]}|${row["Section"]}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(row);
  }

  Object.entries(groups).forEach(([key, rowsInGroup]) => {
    const [paramName, section] = key.split('|');
    
    if (!finalJson[paramName]) {
      finalJson[paramName] = { 
        text: {}, 
        dynamicText: {}, 
        tips: {}, 
        causes: {}, 
        additionalSections: [] 
      };
    }
    const param = finalJson[paramName];

    if (section === "text") {
      rowsInGroup.forEach(r => {
        if (r.hasOwnProperty("Language") && r.hasOwnProperty("Text")) {
          param.text[r["Language"]] = r["Text"]; 
        }
      });
    }
    else if (section === "dynamicText") {
      rowsInGroup.forEach(r => {
        if (r.hasOwnProperty("Language") && r.hasOwnProperty("Text")) {
          param.dynamicText[r["Language"]] = r["Text"]; 
        }
      });
    }
    else if (section === "tips") {
      param.tips = { tipsHeader: {}, tipsSvg: [], tipsSvgText: {} };
      const uniqueIcons = new Set();
      const tipsByLang = {};
      const headerByLang = {};

      rowsInGroup.forEach((r) => {
        const rLang = r["Language"];
        if (!rLang) return; // Skip rows without language
        
        if (r.hasOwnProperty("Header")) {
          headerByLang[rLang] = r["Header"];
        }
        
        if (r.hasOwnProperty("List_Item_Text")) {
          if (!tipsByLang[rLang]) tipsByLang[rLang] = [];
          tipsByLang[rLang].push(r["List_Item_Text"] || "");
        } else if (!tipsByLang[rLang] && r.hasOwnProperty("Header")) {
          // This handles the case of { en: [] }
          tipsByLang[rLang] = [];
        }
        
        if (r.hasOwnProperty("List_Item_Icon") && r["List_Item_Icon"]) {
           uniqueIcons.add(r["List_Item_Icon"]);
        }
      });
      
      param.tips.tipsHeader = headerByLang;
      param.tips.tipsSvgText = tipsByLang;
      param.tips.tipsSvg = Array.from(uniqueIcons);
    }
    else if (section.startsWith("causes-")) {
        const causeType = section.split('-')[1];
        
        if (!param.causes[causeType]) {
           param.causes[causeType] = { heading: {}, description: {}, list: [] };
        }
        const causeData = param.causes[causeType];
        const items = {}; 
        
        // Get flat (non-language) data from the first row
        const firstRow = rowsInGroup[0];
        if (firstRow.hasOwnProperty("listType")) causeData.listType = firstRow["listType"];
        
        rowsInGroup.forEach(r => {
            const rLang = r["Language"];
            if (!rLang) return;

            if (r.hasOwnProperty("Header")) causeData.heading[rLang] = r["Header"];
            if (r.hasOwnProperty("Text / Description")) causeData.description[rLang] = r["Text / Description"];
            
            const itemText = r["List_Item_Text"];
            const itemIcon = r["List_Item_Icon"];

            if (r.hasOwnProperty("List_Item_Text") || r.hasOwnProperty("List_Item_Icon")) {
                const itemKey = itemIcon || `item-${Object.keys(items).length}`;
                if (!items[itemKey]) {
                  items[itemKey] = { text: {}, image: itemIcon || "" };
                }
                if (r.hasOwnProperty("List_Item_Text")) {
                  items[itemKey].text[rLang] = itemText || "";
                }
            }
        });
        causeData.list = Object.values(items);
    }
    else if (section.startsWith("additionalSections-")) {
        const index = parseInt(section.split('-')[1], 10);
        
        if (!param.additionalSections[index]) {
            param.additionalSections[index] = { heading: {}, description: {}, list: [] };
        }
        const sectionData = param.additionalSections[index];
        const items = {};
        
        // --- THIS IS THE FIX ---
        // Get all flat (non-language) data from the *first* row
        const firstRow = rowsInGroup[0];
        if (firstRow.hasOwnProperty("Type")) sectionData.type = firstRow["Type"];
        if (firstRow.hasOwnProperty("Value_Dependent")) sectionData.valueDependent = firstRow["Value_Dependent"];
        if (firstRow.hasOwnProperty("Value_Type")) sectionData.valueType = firstRow["Value_Type"];
        if (firstRow.hasOwnProperty("descriptionImage")) sectionData.descriptionImage = firstRow["descriptionImage"];
        if (firstRow.hasOwnProperty("showHeadingIcon")) sectionData.showHeadingIcon = firstRow["showHeadingIcon"];
        if (firstRow.hasOwnProperty("showListIcons")) sectionData.showListIcons = firstRow["showListIcons"];
        if (firstRow.hasOwnProperty("listType")) sectionData.listType = firstRow["listType"];
        // --- END OF FIX ---

        rowsInGroup.forEach(r => {
            const rLang = r["Language"];
            if (!rLang) return;

            if (r.hasOwnProperty("Header")) sectionData.heading[rLang] = r["Header"];
            if (r.hasOwnProperty("Text / Description")) sectionData.description[rLang] = r["Text / Description"];
            
            const itemText = r["List_Item_Text"];
            const itemIcon = r["List_Item_Icon"];

            if (r.hasOwnProperty("List_Item_Text") || r.hasOwnProperty("List_Item_Icon")) {
                const itemKey = itemIcon || `item-${Object.keys(items).length}`;
                if (!items[itemKey]) {
                  items[itemKey] = { text: {}, image: itemIcon || "" };
                }
                if (r.hasOwnProperty("List_Item_Text")) {
                  items[itemKey].text[rLang] = itemText || "";
                }
            }
        });
        sectionData.list = Object.values(items);
    }
  });

  return finalJson;
};


/**
 * Main import handler to be called from your component.
 */
export const handleParameterImport = (e) => {
  const file = e.target.files[0];
  if (!file) {
    return Promise.reject(new Error("No file selected"));
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = evt.target.result;
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        const rows = XLSX.utils.sheet_to_json(worksheet);

        const jsonData = transformRowsToParameterJson(rows);
        
        resolve(jsonData);
      } catch (error) {
        console.error("Error processing Excel file:", error);
        reject(new Error("Failed to read the Excel file. Format may be incorrect."));
      }
    };
    reader.onerror = (err) => {
        reject(new Error("Failed to read file."));
    };
    reader.readAsArrayBuffer(file);
  });
};